import React, { useState, useEffect, useContext } from 'react'
import { useNavigate } from 'react-router-dom'
import axios from 'axios'
import { toast } from 'sonner'
import { useAuth } from '../context/AuthContext'
import { useLanguage } from '../context/LanguageContext'
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card'
import { Button } from '../components/ui/button'
import { Badge } from '../components/ui/badge'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog'
import { 
  ArrowLeft, TrendingUp, TrendingDown, Calendar, Loader2, 
  Trash2, Edit, ChevronRight, Filter, Wallet, RefreshCw
} from 'lucide-react'
import { cn } from '../lib/utils'
import { formatCurrencyDisplay } from '../components/CurrencyInput'

const API = process.env.REACT_APP_BACKEND_URL

export default function InvestmentHistoryPage() {
  const { token } = useAuth()
  const { locale } = useLanguage()
  const lang = locale.startsWith('es') ? 'es' : 'en'
  const navigate = useNavigate()
  
  const [loading, setLoading] = useState(true)
  const [logs, setLogs] = useState([])
  const [accounts, setAccounts] = useState([])
  const [categories, setCategories] = useState([])
  
  // Filters
  const [filterAccount, setFilterAccount] = useState('all')
  const [filterCategory, setFilterCategory] = useState('all')
  const [filterYear, setFilterYear] = useState('all')
  
  // Delete dialog
  const [deleteDialog, setDeleteDialog] = useState({ open: false, log: null })
  const [deleting, setDeleting] = useState(false)
  
  // Available years for filter
  const currentYear = new Date().getFullYear()
  const years = Array.from({ length: 5 }, (_, i) => currentYear - i)
  
  useEffect(() => {
    fetchData()
  }, [token])
  
  const fetchData = async () => {
    try {
      setLoading(true)
      const [logsRes, accountsRes, categoriesRes] = await Promise.all([
        axios.get(`${API}/api/investments/profit-logs`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/api/investments/accounts`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/api/investments/categories`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ])
      
      setLogs(logsRes.data || [])
      setAccounts(accountsRes.data || [])
      setCategories(categoriesRes.data || [])
    } catch (error) {
      console.error('Error fetching data:', error)
      toast.error(lang === 'es' ? 'Error al cargar datos' : 'Error loading data')
    } finally {
      setLoading(false)
    }
  }
  
  const handleDelete = async () => {
    if (!deleteDialog.log) return
    
    try {
      setDeleting(true)
      await axios.delete(`${API}/api/investments/profit-logs/${deleteDialog.log.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      })
      toast.success(lang === 'es' ? 'Registro eliminado' : 'Record deleted')
      setDeleteDialog({ open: false, log: null })
      fetchData()
    } catch (error) {
      console.error('Error deleting:', error)
      toast.error(lang === 'es' ? 'Error al eliminar' : 'Error deleting')
    } finally {
      setDeleting(false)
    }
  }
  
  const formatCurrency = (value, currency = 'MXN') => {
    return formatCurrencyDisplay(value, currency)
  }
  
  const formatCurrencyWithSign = (value, currency = 'MXN') => {
    return formatCurrencyDisplay(value, currency, true)
  }
  
  const getMonthName = (periodo) => {
    if (!periodo) return ''
    const [year, month] = periodo.split('-')
    const date = new Date(parseInt(year), parseInt(month) - 1)
    return date.toLocaleDateString(lang === 'es' ? 'es-MX' : 'en-US', { month: 'short', year: 'numeric' })
  }
  
  // Filter logs
  const filteredLogs = logs.filter(log => {
    if (filterAccount !== 'all' && log.cuenta_id !== filterAccount) return false
    if (filterYear !== 'all' && !log.periodo_mes.startsWith(filterYear)) return false
    if (filterCategory !== 'all') {
      const account = accounts.find(a => a.id === log.cuenta_id)
      if (!account || account.categoria_id !== filterCategory) return false
    }
    return true
  })
  
  // Group logs by month for display
  const groupedLogs = filteredLogs.reduce((groups, log) => {
    const month = log.periodo_mes
    if (!groups[month]) groups[month] = []
    groups[month].push(log)
    return groups
  }, {})
  
  const sortedMonths = Object.keys(groupedLogs).sort((a, b) => b.localeCompare(a))

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-background p-4 pb-24">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate('/inversiones')}
            className="rounded-full"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-xl font-bold">
              {lang === 'es' ? 'Historial de Rendimientos' : 'Profit History'}
            </h1>
            <p className="text-sm text-muted-foreground">
              {filteredLogs.length} {lang === 'es' ? 'registros' : 'records'}
            </p>
          </div>
        </div>
        <Button
          variant="ghost"
          size="icon"
          onClick={fetchData}
          className="rounded-full"
        >
          <RefreshCw className="w-4 h-4" />
        </Button>
      </div>

      {/* Filters */}
      <Card className="mb-4">
        <CardContent className="p-3">
          <div className="flex items-center gap-2 mb-3">
            <Filter className="w-4 h-4 text-muted-foreground" />
            <span className="text-sm font-medium">{lang === 'es' ? 'Filtros' : 'Filters'}</span>
          </div>
          <div className="grid grid-cols-3 gap-2">
            <Select value={filterAccount} onValueChange={setFilterAccount}>
              <SelectTrigger className="h-9 text-xs">
                <SelectValue placeholder={lang === 'es' ? 'Cuenta' : 'Account'} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{lang === 'es' ? 'Todas' : 'All'}</SelectItem>
                {accounts.map(acc => (
                  <SelectItem key={acc.id} value={acc.id}>{acc.nombre}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            <Select value={filterCategory} onValueChange={setFilterCategory}>
              <SelectTrigger className="h-9 text-xs">
                <SelectValue placeholder={lang === 'es' ? 'Categoría' : 'Category'} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{lang === 'es' ? 'Todas' : 'All'}</SelectItem>
                {categories.map(cat => (
                  <SelectItem key={cat.id} value={cat.id}>{cat.nombre}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            <Select value={filterYear} onValueChange={setFilterYear}>
              <SelectTrigger className="h-9 text-xs">
                <SelectValue placeholder={lang === 'es' ? 'Año' : 'Year'} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{lang === 'es' ? 'Todos' : 'All'}</SelectItem>
                {years.map(year => (
                  <SelectItem key={year} value={String(year)}>{year}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Logs List */}
      {sortedMonths.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <Calendar className="w-12 h-12 mx-auto text-muted-foreground/30 mb-4" />
            <p className="text-muted-foreground">
              {lang === 'es' ? 'No hay registros' : 'No records found'}
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {sortedMonths.map(month => (
            <div key={month}>
              <div className="flex items-center gap-2 mb-2">
                <Calendar className="w-4 h-4 text-muted-foreground" />
                <span className="text-sm font-medium text-muted-foreground capitalize">
                  {getMonthName(month)}
                </span>
              </div>
              
              <div className="space-y-2">
                {groupedLogs[month].map(log => (
                  <Card 
                    key={log.id}
                    className="cursor-pointer hover:shadow-md transition-all"
                    onClick={() => navigate(`/inversiones/rendimiento?cuenta=${log.cuenta_id}&periodo=${log.periodo_mes}`)}
                  >
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <Wallet className="w-4 h-4 text-muted-foreground" />
                            <span className="font-medium truncate">{log.cuenta_nombre}</span>
                            <Badge variant="secondary" className="text-xs">
                              {log.moneda}
                            </Badge>
                          </div>
                          {log.categoria_nombre && (
                            <p className="text-xs text-muted-foreground">{log.categoria_nombre}</p>
                          )}
                        </div>
                        
                        <div className="text-right ml-4">
                          {/* Closing Value */}
                          <p className="text-sm font-medium font-mono">
                            {formatCurrency(log.valor_cierre, log.moneda)}
                          </p>
                          
                          {/* Net Profit */}
                          {log.ganancia_neta !== null && log.ganancia_neta !== undefined ? (
                            <div className="flex items-center justify-end gap-1">
                              {log.ganancia_neta >= 0 ? (
                                <TrendingUp className="w-3 h-3 text-emerald-500" />
                              ) : (
                                <TrendingDown className="w-3 h-3 text-rose-500" />
                              )}
                              <span className={cn(
                                "text-xs font-medium font-mono",
                                log.ganancia_neta >= 0 ? "text-emerald-600" : "text-rose-600"
                              )}>
                                {formatCurrencyWithSign(log.ganancia_neta, log.moneda)}
                              </span>
                              {log.porcentaje_rendimiento !== null && (
                                <span className={cn(
                                  "text-xs",
                                  log.porcentaje_rendimiento >= 0 ? "text-emerald-600" : "text-rose-600"
                                )}>
                                  ({log.porcentaje_rendimiento >= 0 ? '+' : ''}{log.porcentaje_rendimiento?.toFixed(1)}%)
                                </span>
                              )}
                            </div>
                          ) : (
                            <span className="text-xs text-muted-foreground">
                              {lang === 'es' ? 'Primer registro' : 'First record'}
                            </span>
                          )}
                        </div>
                        
                        <div className="flex items-center gap-1 ml-2">
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8"
                            onClick={(e) => {
                              e.stopPropagation()
                              setDeleteDialog({ open: true, log })
                            }}
                          >
                            <Trash2 className="w-4 h-4 text-rose-500" />
                          </Button>
                          <ChevronRight className="w-4 h-4 text-muted-foreground" />
                        </div>
                      </div>
                      
                      {/* Additional info row */}
                      {(log.aportaciones > 0 || log.retiros > 0 || log.nota) && (
                        <div className="mt-2 pt-2 border-t flex flex-wrap gap-2 text-xs">
                          {log.aportaciones > 0 && (
                            <Badge variant="outline" className="text-blue-600 border-blue-200 font-mono">
                              +{formatCurrency(log.aportaciones, log.moneda)} {lang === 'es' ? 'aportado' : 'added'}
                            </Badge>
                          )}
                          {log.retiros > 0 && (
                            <Badge variant="outline" className="text-rose-600 border-rose-200 font-mono">
                              -{formatCurrency(log.retiros, log.moneda)} {lang === 'es' ? 'retirado' : 'withdrawn'}
                            </Badge>
                          )}
                          {log.nota && (
                            <span className="text-muted-foreground truncate max-w-[200px]">
                              📝 {log.nota}
                            </span>
                          )}
                        </div>
                      )}
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Delete Dialog */}
      <Dialog open={deleteDialog.open} onOpenChange={(open) => setDeleteDialog({ open, log: deleteDialog.log })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {lang === 'es' ? '¿Eliminar registro?' : 'Delete record?'}
            </DialogTitle>
            <DialogDescription>
              {lang === 'es' 
                ? 'Esta acción no se puede deshacer. El registro de rendimiento será eliminado permanentemente.'
                : 'This action cannot be undone. The profit record will be permanently deleted.'}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => setDeleteDialog({ open: false, log: null })}
            >
              {lang === 'es' ? 'Cancelar' : 'Cancel'}
            </Button>
            <Button
              variant="destructive"
              onClick={handleDelete}
              disabled={deleting}
            >
              {deleting ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                lang === 'es' ? 'Eliminar' : 'Delete'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
